import type { WorkspaceProjection } from '@localagent/sdk';
import { canMoveWorkspaceHere, WorkspaceMoveControl } from './WorkspaceMoveControl';
import { WORKSTATION_CARD_ID } from './workspaceLinks';
import { Badge } from '@localagent/ui/components/badge';
import { Tooltip } from '@localagent/ui/components/tooltip';
import { cn } from '@localagent/ui/lib/utils';
import {
  displayValue,
  filesystemStatusLabel,
  filesystemTone,
  workstationScreenCaption,
  workstationStatusLabel,
  workstationTone,
  type ResourceTone,
} from './workspaceDisplay';

const TONE_LED: Record<ResourceTone, string> = {
  ok: 'bg-success shadow-[0_0_8px_rgba(52,211,153,0.85)]',
  busy: 'bg-warning shadow-[0_0_8px_rgba(251,191,36,0.75)] motion-safe:animate-pulse',
  bad: 'bg-error shadow-[0_0_8px_rgba(239,68,68,0.85)]',
  off: 'bg-muted-foreground/50',
};

const TONE_GLOW: Record<ResourceTone, string> = {
  ok: 'shadow-[0_0_24px_rgba(16,185,129,0.18)]',
  busy: 'shadow-[0_0_24px_rgba(245,158,11,0.16)]',
  bad: 'shadow-[0_0_24px_rgba(239,68,68,0.16)]',
  off: '',
};

const TONE_BADGE: Record<ResourceTone, 'success' | 'warning' | 'destructive' | 'secondary'> = {
  ok: 'success',
  busy: 'warning',
  bad: 'destructive',
  off: 'secondary',
};

function workstationHeadline(projection: WorkspaceProjection): string {
  const { kind, status } = projection.workstation;
  if (kind === 'local') {
    switch (status) {
      case 'connected':
        return 'This computer is attached and ready for the agent.';
      case 'disconnected':
        return 'The assigned computer is offline. Open the desktop app to attach it.';
      case 'error':
      case 'failed':
        return 'This computer reported a failure.';
      case 'retired':
        return 'This computer is no longer assigned.';
      default:
        return `This computer is ${workstationStatusLabel(status)}.`;
    }
  }
  switch (status) {
    case 'running':
      return 'The cloud computer is powered on. The agent can execute here.';
    case 'sleeping':
      return 'The cloud computer is asleep. Wake it to run the agent.';
    case 'starting':
      return 'The cloud computer is waking.';
    case 'pending':
      return 'The cloud computer has not started yet.';
    case 'reclaiming':
      return 'The cloud computer is shutting down and will be reclaimed.';
    case 'error':
    case 'failed':
      return 'The cloud computer failed.';
    case 'retired':
      return 'The cloud computer is no longer assigned.';
    case 'connected':
    case 'disconnected':
      return `The cloud computer is ${workstationStatusLabel(status)}.`;
  }
}

function filesystemHeadline(projection: WorkspaceProjection): string {
  if (projection.binding?.state === 'missing') {
    return 'The bound folder is gone from this computer.';
  }
  switch (projection.filesystem.state) {
    case 'ready':
      return projection.filesystem.kind === 'local'
        ? 'A folder on this computer holds the files the agent can use.'
        : 'A durable cloud volume holds the files the agent can use.';
    case 'provisioning':
      return 'The volume is being prepared. Files are not ready yet.';
    case 'error':
      return 'This volume failed and cannot serve files.';
    case 'retired':
      return 'This volume is no longer assigned.';
  }
}

function LaptopGlyph({
  tone,
  lit,
  caption,
}: {
  tone: ResourceTone;
  lit: boolean;
  caption: string | null;
}) {
  return (
    <div className="flex w-[9.75rem] flex-col items-center" data-testid="workspace-workstation-glyph">
      <div
        className={cn(
          'w-full rounded-t-xl border-2 border-b-0 border-input bg-muted p-1.5',
          TONE_GLOW[tone]
        )}
      >
        <div
          className={cn(
            'relative flex h-[4.75rem] items-center justify-center overflow-hidden rounded-md',
            lit
              ? 'bg-gradient-to-b from-success/20 via-sunken to-sunken'
              : 'bg-sunken'
          )}
        >
          {lit ? (
            <>
              <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(52,211,153,0.08)_1px,transparent_1px)] bg-[size:100%_7px]" />
              <div className="relative font-mono text-micro leading-4 text-success/90">
                <div>agent@workstation</div>
                <div>
                  $<span className="motion-safe:animate-pulse">▌</span>
                </div>
              </div>
            </>
          ) : (
            <span className="font-mono text-micro tracking-[0.2em] text-muted-foreground">{caption}</span>
          )}
        </div>
      </div>
      <div className="relative h-3 w-[10.5rem] rounded-b-md bg-input">
        <div className="absolute inset-x-10 top-1 h-1 rounded-full bg-muted/80" />
        <span className={cn('absolute right-2 top-1 h-1.5 w-1.5 rounded-full', TONE_LED[tone])} />
      </div>
    </div>
  );
}

function CloudComputerGlyph({
  tone,
  lit,
  caption,
}: {
  tone: ResourceTone;
  lit: boolean;
  caption: string | null;
}) {
  return (
    <div className="flex items-end gap-3" data-testid="workspace-workstation-glyph">
      <div className="flex flex-col items-center">
        <div
          className={cn(
            'rounded-lg border-2 border-input bg-muted p-1.5',
            TONE_GLOW[tone]
          )}
        >
          <div
            className={cn(
              'relative flex h-16 w-[6.5rem] items-center justify-center overflow-hidden rounded-sm',
              'bg-sunken'
            )}
          >
            {lit ? (
              <div className="grid grid-cols-6 gap-0.5 p-2">
                {Array.from({ length: 18 }, (_, index) => (
                  <span
                    key={index}
                    className={cn(
                      'h-1.5 w-1.5 rounded-[1px]',
                      tone === 'busy' ? 'bg-warning/70' : 'bg-info/70',
                      index % 3 === 0 && 'opacity-40'
                    )}
                  />
                ))}
              </div>
            ) : (
              <span className="font-mono text-micro tracking-[0.18em] text-muted-foreground">{caption}</span>
            )}
          </div>
        </div>
        <div className="h-2 w-4 bg-input" />
        <div className="h-1.5 w-14 rounded-sm bg-input" />
      </div>
      <div className="flex h-[5.5rem] w-11 flex-col justify-between rounded-md border-2 border-input bg-muted p-1.5">
        <div className="space-y-1">
          <div className="h-1.5 rounded-sm bg-muted-foreground/50" />
          <div className="h-1.5 rounded-sm bg-muted-foreground/50" />
          <div className="h-1.5 rounded-sm bg-muted-foreground/40" />
        </div>
        <span className={cn('h-1.5 w-1.5 self-end rounded-full', TONE_LED[tone])} />
      </div>
    </div>
  );
}

function FolderGlyph({ tone, ready }: { tone: ResourceTone; ready: boolean }) {
  return (
    <div className="relative w-[8.5rem]" data-testid="workspace-filesystem-glyph">
      <div className="absolute left-3 top-0 h-3 w-12 rounded-t-md bg-chart-3/90" />
      <div
        className={cn(
          'mt-2 flex h-[5.25rem] flex-col justify-end rounded-md border-2 p-2.5',
          ready
            ? 'border-chart-3/60 bg-gradient-to-b from-chart-3/15 to-chart-3/25'
            : 'border-input bg-muted',
          TONE_GLOW[tone]
        )}
      >
        <div className="space-y-1.5">
          <div className={cn('h-1.5 rounded-full', ready ? 'w-4/5 bg-chart-3/40' : 'w-3/5 bg-input/50')} />
          <div className={cn('h-1.5 rounded-full', ready ? 'w-3/5 bg-chart-3/30' : 'w-2/5 bg-input/40')} />
          <div className={cn('h-1.5 rounded-full', ready ? 'w-2/3 bg-chart-3/25' : 'w-1/2 bg-input/30')} />
        </div>
      </div>
      <span className={cn('absolute bottom-2 right-2 h-2 w-2 rounded-full', TONE_LED[tone])} />
    </div>
  );
}

function VolumeGlyph({ tone, ready }: { tone: ResourceTone; ready: boolean }) {
  return (
    <div className="flex w-[8.5rem] flex-col items-center" data-testid="workspace-filesystem-glyph">
      <div
        className={cn(
          'relative flex h-[6.25rem] w-[6.25rem] items-center justify-center rounded-2xl border-2',
          ready
            ? 'border-chart-5/50 bg-gradient-to-br from-chart-5/15 to-muted'
            : 'border-input bg-muted',
          TONE_GLOW[tone]
        )}
      >
        <div
          className={cn(
            'flex h-16 w-16 items-center justify-center rounded-full border-[6px]',
            ready
              ? 'border-chart-5/60'
              : 'border-input/70',
            tone === 'busy' && 'motion-safe:animate-spin'
          )}
          style={tone === 'busy' ? { animationDuration: '3s' } : undefined}
        >
          <div
            className={cn(
              'h-5 w-5 rounded-full',
              ready ? 'bg-chart-5/80' : 'bg-input'
            )}
          />
        </div>
        <span className={cn('absolute bottom-2 right-2 h-2 w-2 rounded-full', TONE_LED[tone])} />
      </div>
    </div>
  );
}

function workstationScreenLit(status: WorkspaceProjection['workstation']['status']): boolean {
  return status === 'connected' || status === 'running' || status === 'starting';
}

function filesystemReady(projection: WorkspaceProjection): boolean {
  return projection.filesystem.state === 'ready' && projection.binding?.state !== 'missing';
}

export function WorkspaceWorkstationCard({ projection }: { projection: WorkspaceProjection }) {
  const tone = workstationTone(projection.workstation.status);
  const lit = workstationScreenLit(projection.workstation.status);
  const caption = workstationScreenCaption(
    projection.workstation.status,
    projection.workstation.kind
  );
  const kindLabel = projection.workstation.kind === 'local' ? 'Local computer' : 'Cloud computer';

  return (
    <article
      id={WORKSTATION_CARD_ID}
      className="flex flex-col rounded-lg bg-card p-4"
      data-testid="workspace-workstation-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold">Workstation</h4>
          <p className="mt-0.5 text-caption text-muted-foreground">The machine the agent runs on</p>
        </div>
        <Badge variant={TONE_BADGE[tone]}>{workstationStatusLabel(projection.workstation.status)}</Badge>
      </div>
      <div className="mt-4 flex min-h-[7.5rem] items-center justify-center">
        {projection.workstation.kind === 'local' ? (
          <LaptopGlyph tone={tone} lit={lit} caption={caption} />
        ) : (
          <CloudComputerGlyph tone={tone} lit={lit} caption={caption} />
        )}
      </div>
      <p className="mt-4 text-body">{workstationHeadline(projection)}</p>
      <dl className="mt-3 space-y-1 text-caption text-muted-foreground">
        <div className="flex justify-between gap-3">
          <dt>Kind</dt>
          <dd className="font-medium text-foreground">{kindLabel}</dd>
        </div>
        {projection.workstation.device_id && (
          <div className="flex justify-between gap-3">
            <dt>Assigned device</dt>
            <dd className="truncate font-mono text-micro text-foreground">
              {projection.workstation.device_id}
            </dd>
          </div>
        )}
        <div className="flex justify-between gap-3">
          <dt>Agent execution</dt>
          <dd className="font-medium text-foreground">
            {projection.capabilities.execution ? 'Available' : 'Unavailable'}
          </dd>
        </div>
      </dl>
      {projection.workstation.environment_unsaved && (
        <p className="mt-3 rounded-md bg-warning-subtle px-2 py-1.5 text-caption">
          Runtime changes on this computer are not saved yet.
        </p>
      )}
      {projection.workstation.failure_reason && (
        <p className="mt-3 text-body text-destructive">{projection.workstation.failure_reason}</p>
      )}
      {canMoveWorkspaceHere(projection) && <WorkspaceMoveControl projection={projection} />}
    </article>
  );
}

export function WorkspaceFilesystemCard({ projection }: { projection: WorkspaceProjection }) {
  const tone = filesystemTone(projection.filesystem.state, projection.binding?.state);
  const ready = filesystemReady(projection);
  const kindLabel = projection.filesystem.kind === 'local' ? 'Folder on this computer' : 'Cloud volume';

  return (
    <article
      className="flex flex-col rounded-lg bg-card p-4"
      data-testid="workspace-filesystem-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="font-semibold">Filesystem</h4>
          <p className="mt-0.5 text-caption text-muted-foreground">The files the agent can use</p>
        </div>
        <Badge variant={TONE_BADGE[tone]}>{filesystemStatusLabel(projection)}</Badge>
      </div>
      <div className="mt-4 flex min-h-[7.5rem] items-center justify-center">
        {projection.filesystem.kind === 'local' ? (
          <FolderGlyph tone={tone} ready={ready} />
        ) : (
          <VolumeGlyph tone={tone} ready={ready} />
        )}
      </div>
      <p className="mt-4 text-body">{filesystemHeadline(projection)}</p>
      <dl className="mt-3 space-y-1 text-caption text-muted-foreground">
        <div className="flex justify-between gap-3">
          <dt>Kind</dt>
          <dd className="font-medium text-foreground">{kindLabel}</dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>Read</dt>
          <dd className="font-medium text-foreground">
            {projection.capabilities.content_readable ? 'Mounted' : 'Unavailable'}
          </dd>
        </div>
        <div className="flex justify-between gap-3">
          <dt>Write</dt>
          <dd className="font-medium text-foreground">
            {projection.capabilities.content_writable ? 'Mounted' : 'Unavailable'}
          </dd>
        </div>
      </dl>
      {projection.binding && (
        <div className="mt-3 rounded-md bg-muted px-2 py-1.5 text-caption" data-testid="workspace-binding">
          <p className="font-medium text-foreground">
            {projection.binding.folder_path} · {displayValue(projection.binding.state)}
          </p>
        </div>
      )}
      {projection.filesystem.failure_reason && (
        <p className="mt-3 text-body text-destructive">{projection.filesystem.failure_reason}</p>
      )}
    </article>
  );
}

export function WorkspaceResourceCards({ projection }: { projection: WorkspaceProjection }) {
  return (
    <div className="mt-4 grid gap-3 lg:grid-cols-2" data-testid="workspace-resource-cards">
      <WorkspaceWorkstationCard projection={projection} />
      <WorkspaceFilesystemCard projection={projection} />
    </div>
  );
}

function MiniLaptop({ tone }: { tone: ResourceTone }) {
  return (
    <span className="relative inline-flex h-4 w-6 items-end" aria-hidden>
      <span className="absolute inset-x-0.5 top-0 h-3 rounded-[2px] border border-input bg-sunken" />
      <span className="h-0.5 w-full rounded-sm bg-input" />
      <span className={cn('absolute right-0.5 top-0.5 h-1 w-1 rounded-full', TONE_LED[tone])} />
    </span>
  );
}

function MiniTower({ tone }: { tone: ResourceTone }) {
  return (
    <span className="relative inline-flex h-4 w-3" aria-hidden>
      <span className="h-full w-full rounded-[2px] border border-input bg-muted" />
      <span className={cn('absolute bottom-0.5 right-0.5 h-1 w-1 rounded-full', TONE_LED[tone])} />
    </span>
  );
}

function MiniFolder({ tone }: { tone: ResourceTone }) {
  return (
    <span className="relative inline-flex h-4 w-5 items-end" aria-hidden>
      <span className="absolute left-0.5 top-0 h-1.5 w-2 rounded-t-[2px] bg-chart-3/90" />
      <span className="h-3 w-full rounded-[2px] border border-chart-3/40 bg-chart-3/25" />
      <span className={cn('absolute bottom-0.5 right-0.5 h-1 w-1 rounded-full', TONE_LED[tone])} />
    </span>
  );
}

function MiniVolume({ tone }: { tone: ResourceTone }) {
  return (
    <span className="relative inline-flex h-4 w-4 items-center justify-center" aria-hidden>
      <span className="h-3.5 w-3.5 rounded-full border-2 border-chart-5/70" />
      <span className={cn('absolute h-1.5 w-1.5 rounded-full', TONE_LED[tone])} />
    </span>
  );
}

export function WorkspaceResourceSummary({
  projection,
  className,
}: {
  projection: WorkspaceProjection;
  className?: string;
}) {
  const machineTone = workstationTone(projection.workstation.status);
  const volumeTone = filesystemTone(projection.filesystem.state, projection.binding?.state);

  return (
    <div
      className={cn('flex flex-wrap items-center gap-3 text-caption', className)}
      data-testid="workspace-resource-summary"
    >
      <span className="inline-flex items-center gap-1.5" data-testid="workspace-workstation-summary">
        {projection.workstation.kind === 'local' ? (
          <MiniLaptop tone={machineTone} />
        ) : (
          <MiniTower tone={machineTone} />
        )}
        <span className="text-muted-foreground">
          {projection.workstation.kind === 'local' ? 'Computer' : 'Cloud computer'}
        </span>
        <span className="font-medium">{workstationStatusLabel(projection.workstation.status)}</span>
      </span>
      <span className="inline-flex items-center gap-1.5" data-testid="workspace-filesystem-summary">
        {projection.filesystem.kind === 'local' ? (
          <MiniFolder tone={volumeTone} />
        ) : (
          <MiniVolume tone={volumeTone} />
        )}
        <span className="text-muted-foreground">
          {projection.filesystem.kind === 'local' ? 'Folder' : 'Cloud volume'}
        </span>
        <span className="font-medium">{filesystemStatusLabel(projection)}</span>
      </span>
    </div>
  );
}

function MiniIndex({ tone }: { tone: ResourceTone }) {
  return (
    <span className="relative inline-flex h-4 w-4 items-center justify-center" aria-hidden>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.25"
        strokeLinecap="round"
        strokeLinejoin="round"
        className="text-muted-foreground"
      >
        <circle cx="10.5" cy="10.5" r="6.5" />
        <path d="m20 20-4.5-4.5" />
      </svg>
      <span className={cn('absolute left-[5px] top-[5px] h-1.5 w-1.5 rounded-full', TONE_LED[tone])} />
    </span>
  );
}

function indexTone(index: WorkspaceProjection['index']): ResourceTone {
  if (index.health !== 'ok') return 'bad';
  return index.progress === 'indexing' ? 'busy' : 'ok';
}

function indexDetail(index: WorkspaceProjection['index']): string {
  const counts = `${index.indexed} indexed, ${index.queued} queued, ${index.embedding} embedding`;
  if (index.health !== 'ok') return `Search index ${displayValue(index.health)} — ${counts}`;
  if (index.progress === 'indexing') return `Search index updating — ${counts}`;
  return `Search index up to date — ${index.indexed} indexed`;
}

function withFailure(detail: string, failure: string | null | undefined): string {
  return failure ? `${detail} ${failure}` : detail;
}

export function WorkspaceStatusIcons({
  projection,
  showIndex,
}: {
  projection: WorkspaceProjection;
  showIndex: boolean;
}) {
  const local = projection.workstation.kind === 'local';
  const localFolder = projection.filesystem.kind === 'local';
  const machineTone = workstationTone(projection.workstation.status);
  const volumeTone = filesystemTone(projection.filesystem.state, projection.binding?.state);
  const machineDetail = withFailure(
    `${local ? 'Computer' : 'Cloud computer'} ${workstationStatusLabel(projection.workstation.status)} — ${workstationHeadline(projection)}`,
    projection.workstation.failure_reason,
  );
  const volumeDetail = withFailure(
    `${localFolder ? 'Folder' : 'Cloud volume'} ${filesystemStatusLabel(projection)} — ${filesystemHeadline(projection)}`,
    projection.filesystem.failure_reason,
  );

  return (
    <span className="inline-flex items-center gap-1.5" data-testid="workspace-status-icons">
      <Tooltip label={machineDetail}>
        <span
          tabIndex={0}
          data-testid="workspace-status-workstation"
          data-tone={machineTone}
          className="inline-flex rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {local ? <MiniLaptop tone={machineTone} /> : <MiniTower tone={machineTone} />}
        </span>
      </Tooltip>
      <Tooltip label={volumeDetail}>
        <span
          tabIndex={0}
          data-testid="workspace-status-filesystem"
          data-tone={volumeTone}
          className="inline-flex rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {localFolder ? <MiniFolder tone={volumeTone} /> : <MiniVolume tone={volumeTone} />}
        </span>
      </Tooltip>
      {showIndex && (
        <Tooltip label={indexDetail(projection.index)}>
          <span
            tabIndex={0}
            data-testid="workspace-status-index"
            data-tone={indexTone(projection.index)}
            className="inline-flex rounded p-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <MiniIndex tone={indexTone(projection.index)} />
          </span>
        </Tooltip>
      )}
    </span>
  );
}
